import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch } from "react-redux";
import { useHotkeys } from "react-hotkeys-hook";
import errorToast from "../../../../components/helper-components/error-toast";
import { warehouseBinItemsListAction } from "../../../../redux/actions/inventory-action";
import { toNumber } from "../../../../utils/purchase-return-calculations";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fieldLabelClass =
  "text-[10px] uppercase tracking-wide text-[var(--trans-stats-con-label-fg)] font-semibold";
const fieldValueClass = "text-sm font-semibold text-[var(--heading-color)] mt-1";

const BinItemCard = ({ item }) => {
  const isExpired = (() => {
    const parsed = new Date(item.item_exp_date);
    return !Number.isNaN(parsed.getTime()) && parsed < new Date();
  })();

  return (
    <div className="bg-[var(--create-table-row-bg)] border border-[var(--create-table-border)] border-l-4 border-l-[var(--primary)] rounded-md p-3 flex flex-col gap-2">
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-[var(--status-badge-blue-bg)] text-[var(--status-badge-blue-fg)] text-xs font-semibold px-2 py-0.5 rounded">
            {item.item_batch_number || "-"}
          </span>
          <span className="bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)] border border-[var(--status-badge-red-border)] text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
            Exp {formatDate(item.item_exp_date)}
            {isExpired ? " · expired" : ""}
          </span>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-[var(--heading-color)]">
            {item.item_name || "-"}
          </div>
          <div className="text-xs text-[var(--description-color)] whitespace-nowrap">
            {item.item_code || "-"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-[var(--create-table-border)]">
        <div>
          <span className={fieldLabelClass}>Item Code</span>
          <div className={fieldValueClass}>{item.item_code || "-"}</div>
        </div>
        <div>
          <span className={fieldLabelClass}>Batch No</span>
          <div className={fieldValueClass}>{item.item_batch_number || "-"}</div>
        </div>
        <div>
          <span className={fieldLabelClass}>Expiry</span>
          <div className={fieldValueClass}>{formatDate(item.item_exp_date)}</div>
        </div>
        <div>
          <span className={fieldLabelClass}>Qty</span>
          <div className="text-sm font-bold text-[var(--primary)] mt-1">
            {toNumber(item.item_quantity)}
          </div>
        </div>
      </div>
    </div>
  );
};

const BinItemsSidePanel = ({ open, setOpen, bin }) => {
  const close = useRef(null);
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !bin?.bin_no_id) return;
    setItems([]);
    setLoading(true);
    dispatch(
      warehouseBinItemsListAction(
        { bin_no_id: bin.bin_no_id },
        (res) => {
          setItems(res?.data || []);
          setLoading(false);
        },
        (err) => {
          setLoading(false);
          errorToast(err?.data?.message || err?.message || "Failed to load bin items");
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bin?.bin_no_id]);

  useHotkeys("esc", () => {
    if (close.current) {
      close.current.click();
    }
  });

  // Bin number is the same for every row in the response; fall back to the
  // clicked bin when the bin is empty.
  const binNo = items[0]?.bin_location_no || bin?.bin_location_no || "-";
  const totalQty = items.reduce((sum, item) => sum + toNumber(item.item_quantity), 0);
  const uniqueItems = new Set(items.map((item) => item.item_code)).size;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[640px] bg-[var(--create-table-row-bg)] rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                {/* Header */}
                <div className="p-4 flex justify-between items-start bg-[var(--create-table-header-bg)]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[var(--create-table-header-fg)] font-semibold">
                      Bin Items
                    </div>
                    <div className="text-base sm:text-lg font-bold text-[var(--create-table-header-fg)] leading-snug">
                      {binNo}
                    </div>
                  </div>
                  <button
                    ref={close}
                    onClick={() => setOpen(false)}
                    className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-white/10 text-[var(--create-table-header-fg)] hover:bg-white/20"
                  >
                    ✕
                  </button>
                </div>

                {/* Stats bar */}
                <div className="px-4 py-3 bg-[var(--slide-panel-head-bg)] border-b border-[var(--slide-panel-border)]">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <div className={fieldLabelClass}>Bin No</div>
                      <div className="text-2xl font-bold text-[var(--trans-stats-con-value-fg)]">
                        {binNo}
                      </div>
                    </div>
                    <div>
                      <div className={fieldLabelClass}>Items</div>
                      <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                        {uniqueItems}
                      </div>
                    </div>
                    <div>
                      <div className={fieldLabelClass}>Batches</div>
                      <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                        {items.length}
                      </div>
                    </div>
                    <div>
                      <div className={fieldLabelClass}>Total Qty</div>
                      <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                        {totalQty}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="space-y-3 p-4 flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="text-sm text-[var(--description-color)] text-center py-8">
                      Loading items…
                    </div>
                  ) : items.length === 0 ? (
                    <div className="text-sm text-[var(--description-color)] text-center py-8">
                      No items in this bin.
                    </div>
                  ) : (
                    items.map((item, index) => (
                      <BinItemCard
                        key={`${item.item_code}_${item.item_batch_number}_${index}`}
                        item={item}
                      />
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-[var(--slide-panel-border)] bg-[var(--create-table-row-bg)] sticky bottom-0 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-6 py-2 rounded-md fw_600 bg-[var(--primary)] text-white hover:opacity-90"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BinItemsSidePanel;
